import type { QueryClient } from '@tanstack/react-query';
import type { BootstrapStep } from '@doccareplus/kernel/application/bootstrap';
import type { AppDispatch } from '@doccareplus/kernel/application';
import { membershipsRefreshed } from '@doccareplus/kernel/application';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import { ensureClinicAccess } from '@features/clinic-access';

export interface WorkspaceBootstrapDeps {
  queryClient: QueryClient;
  activeContext: { clinicId?: string; role?: string } | null | undefined;
  dispatch: AppDispatch;
  t: (key: string, opts?: { defaultValue?: string }) => string;
}

/** Query key + freshness window for the session snapshot, so concurrent/duplicate
 * bootstrap runs (e.g. React StrictMode's double-invoke in dev) share one request
 * instead of hitting /auth/session twice. */
const SESSION_QUERY_KEY = ['auth-session'] as const;
const SESSION_STALE_TIME = 60_000;

/**
 * Ordered startup steps for the authenticated workspace, run behind the global
 * loader before the shell paints (see WorkspaceBootstrapGate).
 *
 * To add a new bootstrap loading phase (e.g. workspace branding, feature flags,
 * unread counts), append a step here — its `message` shows under the loader
 * while its `run()` is in flight, and returning `false` short-circuits the rest.
 * Keep steps ordered by dependency.
 */
export function createWorkspaceBootstrapSteps({
  queryClient,
  activeContext,
  dispatch,
  t,
}: WorkspaceBootstrapDeps): BootstrapStep[] {
  return [
    {
      id: 'session-sync',
      message: t('workspace.loading', { defaultValue: 'Loading your workspace…' }),
      run: async () => {
        // Re-pull memberships from the server so roles assigned since login (to this or another
        // logged-in user) become selectable on load — the persisted Redux/localStorage copy is
        // otherwise only refreshed by a login or a 401-triggered token refresh. Routed through the
        // query cache so a duplicate bootstrap run (StrictMode in dev) reuses one request rather
        // than hitting /auth/session twice. Non-blocking: on failure we keep the persisted memberships.
        try {
          const { memberships } = await queryClient.ensureQueryData({
            queryKey: SESSION_QUERY_KEY,
            queryFn: () => authApi.getSession(),
            staleTime: SESSION_STALE_TIME,
          });
          dispatch(membershipsRefreshed(memberships));
        } catch {
          // Keep the persisted session; a transient failure must not block or clear the workspace.
        }
        return true;
      },
    },
    {
      id: 'workspace-access',
      message: t('workspace.loading', { defaultValue: 'Loading your workspace…' }),
      run: async () => {
        // Prefetch clinic access so the shell's permission-gated nav renders
        // complete on first paint instead of "popping in".
        await ensureClinicAccess(queryClient, activeContext);
        return true;
      },
    },
  ];
}
